import React, { memo, useCallback } from 'react';

import { Box } from '@mui/material';
import FormGeneratorSwitchCDS from '@sharedCR/coreDesignSystem/formGenerator/FormGeneratorSwitchCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FieldTypesEnum, mersPreviewOperationOptions } from 'constants/loanActionEditor';

import { loanManagementTestIds } from '../../loanManagement.testIds';
import useFormContext from '../hooks/useFormContext';
import { wrapperClassname } from '../LoanActionItem.constants';
import { DynamicValueInput } from '../LoanActionItems';

import { getMersPreviewKindConfig } from './LoanActionMersPreviewRow.config';
import {
  NO_OPERATION_KIND_LABEL,
  NO_OPERATION_KIND_OPTIONS,
  OPERATION_LABEL,
  PREVIEW_ROW_DEFAULTS,
} from './LoanActionMersPreviewRow.constants';

// Preview MERS: the operation to preview, then the kind (and switch) of the MERS action it previews — Register with
// MERS's pre-closing, Transfer with MERS's flow. A preview sends nothing, so there is no task to raise on a refusal.
const LoanActionMersPreviewRow = ({ index, rootPath, isDisabled = false, isEditAllowed }) => {
  const { form, setInForm } = useFormContext();

  const fieldPath = `${rootPath}[${index}]`;
  const operationFieldPath = `${fieldPath}.operation`;
  const kindFieldPath = `${fieldPath}.kind`;

  const operation = _.get(form, operationFieldPath);
  const kind = _.get(form, kindFieldPath);

  const kindConfig = getMersPreviewKindConfig(operation?.value);
  const toggle = kindConfig?.toggle ?? null;
  const toggleFieldPath = toggle ? `${fieldPath}.${toggle.fieldName}` : null;
  const toggleValue = toggle ? _.get(form, toggleFieldPath) : null;

  const isInputDisabled = isDisabled || !isEditAllowed;
  const isToggleHidden = !toggle || !toggle.kinds.includes(kind?.value);

  // Each operation chooses from its own kinds and switches, so a new operation starts from the defaults.
  const onOperationChange = useCallback(() => {
    setInForm(kindFieldPath, PREVIEW_ROW_DEFAULTS.kind);
    setInForm(`${fieldPath}.preClosing`, PREVIEW_ROW_DEFAULTS.preClosing);
    setInForm(`${fieldPath}.flow`, PREVIEW_ROW_DEFAULTS.flow);
  }, [setInForm, kindFieldPath, fieldPath]);

  const onToggle = useCallback(
    (__, checked) => setInForm(toggleFieldPath, checked),
    [setInForm, toggleFieldPath],
  );

  return (
    <Box className={wrapperClassname}>
      <DynamicValueInput
        required
        disabled={isInputDisabled}
        label={OPERATION_LABEL}
        name={operationFieldPath}
        options={mersPreviewOperationOptions}
        type={FieldTypesEnum.SELECT}
        withSortedOptions={false}
        onChange={onOperationChange}
      />

      <DynamicValueInput
        required
        disabled={isInputDisabled || !kindConfig}
        label={kindConfig?.kindLabel ?? NO_OPERATION_KIND_LABEL}
        name={kindFieldPath}
        options={kindConfig?.kindOptions ?? NO_OPERATION_KIND_OPTIONS}
        type={FieldTypesEnum.SELECT}
      />

      <FormGeneratorSwitchCDS
        dataTestId={loanManagementTestIds.getMersToggleSwitchId(fieldPath)}
        disabled={isInputDisabled}
        isHidden={isToggleHidden}
        label={toggle?.label ?? ''}
        name={toggleFieldPath}
        value={!!toggleValue}
        onChange={onToggle}
      />
    </Box>
  );
};

LoanActionMersPreviewRow.propTypes = {
  rootPath: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool,
};

export default memo(LoanActionMersPreviewRow);
